import React, { useState } from 'react';
import { SCENARIO_REGISTRY } from '../model/scenarioRegistry';
import './PolicyControls.css';

// Reusable Slider Component
const SliderControl = ({ label, value, min, max, step = 0.01, format = 'number', suffix = '', tooltip, onChange, isSpeculative, warning }) => {
    const displayValue = format === 'percent'
        ? `${(value * 100).toFixed(step < 0.01 ? 1 : 0)}%`
        : format === 'currency'
            ? `$${(value / 1e9).toFixed(1)}B`
            : format === 'currency-raw'
                ? `$${Math.round(value).toLocaleString()}`
                : `${value.toLocaleString()}${suffix}`;

    return (
        <label className={`slider-control ${isSpeculative ? 'speculative' : ''}`}>
            <div className="slider-header">
                <span className="slider-label">
                    {label}
                    {tooltip && <span className="info-icon" title={tooltip}>ⓘ</span>}
                </span>
                <span className="slider-value">{displayValue}</span>
            </div>
            {isSpeculative && warning && (
                <div className="speculative-warning">{warning}</div>
            )}
            <input
                type="range"
                min={format === 'percent' ? min * 100 : min}
                max={format === 'percent' ? max * 100 : max}
                step={format === 'percent' ? step * 100 : step}
                value={format === 'percent' ? value * 100 : value}
                onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    onChange(format === 'percent' ? val / 100 : val);
                }}
            />
        </label>
    );
};

// Simple Select Component
const SelectControl = ({ label, value, options, tooltip, onChange }) => {
    return (
        <label className="slider-control">
            <div className="slider-header">
                <span className="slider-label">
                    {label}
                    {tooltip && <span className="info-icon" title={tooltip}>ⓘ</span>}
                </span>
                <span className="slider-value">{value}</span>
            </div>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="policy-select"
            >
                {options.map(opt => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
        </label>
    );
};

// Boolean toggle
const ToggleControl = ({ label, checked, tooltip, onChange }) => {
    return (
        <label className="slider-control" style={{ cursor: 'pointer' }}>
            <div className="slider-header">
                <span className="slider-label">
                    {label}
                    {tooltip && <span className="info-icon" title={tooltip}>ⓘ</span>}
                </span>
                <span className="slider-value">{checked ? 'On' : 'Off'}</span>
            </div>
            <input
                type="checkbox"
                checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
                style={{ marginTop: '10px', transform: 'scale(1.1)' }}
            />
        </label>
    );
};

export default function PolicyControls({ config, onChange, activeScenario, onApplyScenario, onReset }) {
    const [activeTab, setActiveTab] = useState('finance');

    const updateConfig = (key, value) => {
        onChange({ ...config, [key]: value });
    };

    const tabs = [
        { id: 'finance', label: 'Financing', icon: '💰' },
        { id: 'provider', label: 'Provider', icon: '🏥' },
        { id: 'behavior', label: 'Behavior', icon: '🧠' },
        { id: 'compounding', label: 'Compounding', icon: '🔄' },
        { id: 'economy', label: 'Economy', icon: '📈' },
        { id: 'shocks', label: 'Shocks', icon: '⚡' },
        { id: 'presets', label: 'Scenarios', icon: '📋' }
    ];

    return (
        <div className="policy-controls">
            <div className="policy-tabs">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        className={`policy-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        <span className="tab-icon">{tab.icon}</span>
                        <span className="tab-label">{tab.label}</span>
                    </button>
                ))}
            </div>

            <div className="controls-content">
                {/* Global Settings (Always Visible) */}
                <div className="control-group" style={{ marginBottom: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '1rem' }}>
                    <h3><span className="control-group-icon">⚙️</span> Simulation Settings</h3>
                    <SliderControl
                        label="Simulation Period"
                        value={config.simulationYears || 10}
                        min={5} max={21} step={1}
                        format="number"
                        suffix=" years"
                        tooltip="Total years to simulate (budget window)"
                        isSpeculative={(config.simulationYears || 10) > 10}
                        warning="⚠️ HIGHLY SPECULATIVE"
                        onChange={v => updateConfig('simulationYears', v)}
                    />
                </div>

                {/* FINANCING TAB */}
                {activeTab === 'finance' && (
                    <div className="control-group">
                        <h3><span className="control-group-icon">💰</span> Tax Rates</h3>
                        <SliderControl
                            label="Employer Payroll Tax"
                            value={config.employerRate}
                            min={0} max={0.15} step={0.005}
                            format="percent"
                            onChange={v => updateConfig('employerRate', v)}
                        />

                        <h3><span className="control-group-icon">🏦</span> Trust Fund Seed</h3>
                        <SliderControl
                            label="Initial Capital Injection"
                            value={config.initialTrustFundSeed || 0}
                            min={0} max={1500000000000} step={250000000000}
                            format="currency"
                            suffix="B"
                            tooltip="One-time appropriations to cover Year 1-2 deficits"
                            onChange={v => updateConfig('initialTrustFundSeed', v)}
                        />
                        <h3><span className="control-group-icon">🛏️</span> Long-Term Care (LTSS)</h3>
                        <SliderControl
                            label="LTSS Cost Multiplier"
                            value={config.ltssMultiplier ?? 1.0}
                            min={0.5} max={1.5} step={0.05}
                            format="percent"
                            tooltip="Scale pipeline LTC projections (100% = as modeled, 80% = lower uptake, 120% = higher). Only applies when using pipeline data."
                            onChange={v => updateConfig('ltssMultiplier', v)}
                        />
                        <SliderControl
                            label="Individual Income Tax"
                            value={config.baseIndividualRate}
                            min={0} max={0.10} step={0.005}
                            format="percent"
                            onChange={v => updateConfig('baseIndividualRate', v)}
                        />
                        <SliderControl
                            label="Trust Fund Reserve Cap"
                            value={config.reserveFundCap ?? 1.5}
                            min={0.10} max={2.0} step={0.05}
                            format="percent"
                            tooltip="If reserves exceed this % of annual outlays, federal contributions are reduced. (Bill standard: 10%)"
                            onChange={v => updateConfig('reserveFundCap', v)}
                        />
                        <SliderControl
                            label="Capital Income Coverage"
                            value={config.capitalCoverage}
                            min={0} max={1.0} step={0.05}
                            format="percent"
                            tooltip="Share of capital gains/dividends included in the base (0% = wage only, 100% = full AGI)"
                            onChange={v => updateConfig('capitalCoverage', v)}
                        />
                        <div style={{ marginTop: '15px', marginBottom: '15px', borderTop: '1px dashed #cbd5e1', paddingTop: '15px' }}>
                            <span className="control-section-label">Tax Base Definitions (Thresholds)</span>

                            <SliderControl
                                label="Standard Income Exemption"
                                value={config.incomeExemptionThreshold ?? 30120}
                                min={0} max={100000} step={1000}
                                format="currency-raw"
                                suffix=""
                                tooltip="Annual income below this amount is tax-free (Standard Deduction)."
                                onChange={v => updateConfig('incomeExemptionThreshold', v)}
                            />

                            <SliderControl
                                label="Inv. Income Tax Threshold"
                                value={config.investmentTaxThreshold ?? 0}
                                min={0} max={1000000} step={10000}
                                format="currency-raw"
                                suffix=""
                                tooltip="Investment income is only taxed for filers earning above this amount."
                                onChange={v => updateConfig('investmentTaxThreshold', v)}
                            />
                        </div>
                        <hr className="divider" />
                        <h3><span className="control-group-icon">🛡️</span> Evasion & Avoidance</h3>
                        <SliderControl
                            label="Tax Evasion Rate"
                            value={config.taxEvasionRate}
                            min={0} max={0.30} step={0.01}
                            format="percent"
                            tooltip="General tax gap / non-compliance"
                            onChange={v => updateConfig('taxEvasionRate', v)}
                        />
                        <SliderControl
                            label="Surtax Avoidance"
                            value={config.surtaxAvoidance}
                            min={0} max={1.0} step={0.05}
                            format="percent"
                            tooltip="Income shifting via non-realization (Buy/Borrow/Die) or corporate retention to avoid AGI recognition."
                            onChange={v => updateConfig('surtaxAvoidance', v)}
                        />

                        <hr className="divider" />
                        <h3><span className="control-group-icon">⚖️</span> Stabilizer Enforcement (Sec 206)</h3>
                        <SliderControl
                            label="Sec 206 Enforcement Strength"
                            value={config.enforceTrustFundRules ?? 1}
                            min={0} max={1} step={0.5}
                            format="percent"
                            tooltip="0% = no automatic stabilizer steps are applied. 50% = partial/irregular application. 100% = as-written enforcement."
                            onChange={v => updateConfig('enforceTrustFundRules', v)}
                        />
                        <ToggleControl
                            label="Allow Automatic Rate Increases"
                            checked={config.allowAutoIncrease !== false}
                            tooltip="Stress test: if Off, models Congress/politics blocking upward auto-adjustments even when reserves fall below 5%."
                            onChange={v => updateConfig('allowAutoIncrease', v)}
                        />
                        <ToggleControl
                            label="Allow Automatic Rate Decreases"
                            checked={config.allowAutoDecrease !== false}
                            tooltip="If Off, models blocking downward auto-adjustments even when reserves exceed 10%."
                            onChange={v => updateConfig('allowAutoDecrease', v)}
                        />
                    </div>
                )}

                {/* PROVIDER TAB */}
                {activeTab === 'provider' && (
                    <div className="control-group">
                        <h3><span className="control-group-icon">💵</span> Provider Payments</h3>
                        <SliderControl
                            label="Payment Floor (vs Medicare)"
                            value={config.providerPaymentFloor}
                            min={0.9} max={1.2} step={0.01}
                            format="percent"
                            onChange={v => updateConfig('providerPaymentFloor', v)}
                        />
                        <SliderControl
                            label="Rural Uplift"
                            value={config.ruralUplift}
                            min={1.0} max={1.5} step={0.05}
                            format="percent"
                            onChange={v => updateConfig('ruralUplift', v)}
                        />
                        <hr className="divider" />
                        <h3>Network & Access</h3>
                        <SliderControl
                            label="Participation Rate"
                            value={config.providerParticipationRate}
                            min={0.5} max={1.0} step={0.05}
                            format="percent"
                            tooltip="Providers accepting SAFECARE rates"
                            onChange={v => updateConfig('providerParticipationRate', v)}
                        />
                        <hr className="divider" />
                        <h3><span className="control-group-icon">🏚️</span> Safety Net Infrastructure</h3>
                        <SliderControl
                            label="Capital Grants Budget"
                            value={config.capitalGrantsAnnual || 100000000000}
                            min={50000000000} max={1000000000000} step={10000000000}
                            format="currency"
                            suffix="B"
                            tooltip="Annual funding for rural hospitals (Sec 604)"
                            onChange={v => updateConfig('capitalGrantsAnnual', v)}
                        />
                        <SliderControl
                            label="Medical Debt Redemption"
                            value={config.debtRedemptionBudget || 5000000000}
                            min={5000000000} max={20000000000} step={1000000000}
                            format="currency"
                            suffix="B"
                            tooltip="Annual budget to forgive medical debt (Sec 701)"
                            onChange={v => updateConfig('debtRedemptionBudget', v)}
                        />
                    </div>
                )}

                {/* BEHAVIOR TAB */}
                {activeTab === 'behavior' && (
                    <div className="control-group">
                        <h3><span className="control-group-icon">🧠</span> Patient Behavior</h3>
                        <SliderControl
                            label="Utilization Surge"
                            value={config.utilizationSurge}
                            min={0} max={0.50} step={0.05}
                            format="percent"
                            tooltip="Demand spike from newly insured (Years 1-3)"
                            onChange={v => updateConfig('utilizationSurge', v)}
                        />
                        <SliderControl
                            label="Medical Tourism Leakage"
                            value={config.medicalTourismLeakage}
                            min={0} max={0.10} step={0.01}
                            format="percent"
                            tooltip="Wealthy patients seeking care abroad"
                            onChange={v => updateConfig('medicalTourismLeakage', v)}
                        />
                        <SliderControl
                            label="Supplemental Coverage"
                            value={config.supplementalCoverageRate}
                            min={0} max={0.50} step={0.05}
                            format="percent"
                            onChange={v => updateConfig('supplementalCoverageRate', v)}
                        />
                    </div>
                )}

                {/* COMPOUNDING TAB */}
                {activeTab === 'compounding' && (
                    <div className="control-group">
                        <h3><span className="control-group-icon">🔄</span> Compound Effects</h3>
                        <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '1rem' }}>
                            These parameters compound annually, significantly altering long-term outcomes.
                        </p>
                        <SliderControl
                            label="Wage Growth Boost"
                            value={config.wageGrowthDifferential}
                            min={-0.01} max={0.03} step={0.005}
                            format="percent"
                            tooltip="Annual compound wage growth due to lower premiums"
                            onChange={v => updateConfig('wageGrowthDifferential', v)}
                        />
                        <SliderControl
                            label="Consolidation Rate"
                            value={config.providerConsolidationSpeed}
                            min={0} max={0.10} step={0.01}
                            format="percent"
                            tooltip="Annual increase in provider consolidation (Higher costs)"
                            onChange={v => updateConfig('providerConsolidationSpeed', v)}
                        />
                        <SliderControl
                            label="Payroll Base Erosion"
                            value={config.payrollBaseErosion}
                            min={0} max={0.05} step={0.005}
                            format="percent"
                            tooltip="Annual compound decay of tax base (Gig economy shift)"
                            onChange={v => updateConfig('payrollBaseErosion', v)}
                        />
                        <SliderControl
                            label="Innovation Reduction"
                            value={config.drugInnovationReduction}
                            min={0} max={0.50} step={0.05}
                            format="percent"
                            tooltip="Annual compound reduction in new drug entry"
                            onChange={v => updateConfig('drugInnovationReduction', v)}
                        />
                    </div>
                )}

                {/* ECONOMY TAB */}
                {activeTab === 'economy' && (
                    <div className="control-group">
                        <h3>Macroeconomic Factors</h3>
                        <SliderControl
                            label="Admin Efficiency Floor"
                            value={config.adminEfficiencyFloor}
                            min={0.01} max={0.10} step={0.005}
                            format="percent"
                            tooltip="Lowest possible admin cost %"
                            onChange={v => updateConfig('adminEfficiencyFloor', v)}
                        />
                        <SliderControl
                            label="Admin Simplification Enforcement"
                            value={config.enforceAdminSimplification ?? 1}
                            min={0} max={1} step={0.5}
                            format="percent"
                            tooltip="0% = admin simplification fails (admin stays closer to today's dual-system overhead). 100% = full admin burden reduction as intended."
                            onChange={v => updateConfig('enforceAdminSimplification', v)}
                        />
                        <SliderControl
                            label="Feedback Sensitivity"
                            value={config.macroFeedbackSensitivity || 1.0}
                            min={0} max={2.0} step={0.1}
                            format="number"
                            onChange={v => updateConfig('macroFeedbackSensitivity', v)}
                        />

                        <hr className="divider" />
                        <h3><span className="control-group-icon">💊</span> Drug Pricing Regime (Sec 405)</h3>
                        <SelectControl
                            label="Drug Pricing Mode"
                            value={config.drugPricingMode || 'bill'}
                            tooltip="Bill mode uses the lower of (110% IRP) and Domestic Value Benchmark as an aggregate ceiling proxy. Use DVB-only to model IRP invalidation."
                            options={[
                                { value: 'bill', label: 'Bill ceiling (min(IRP, DVB))' },
                                { value: 'irp_only', label: 'IRP only (110% intl median)' },
                                { value: 'dvb_only', label: 'DVB only (IRP invalidated)' }
                            ]}
                            onChange={v => updateConfig('drugPricingMode', v)}
                        />
                        <SliderControl
                            label="International Price (% of US)"
                            value={config.internationalPricePctOfUS ?? 0.40}
                            min={0.20} max={0.80} step={0.05}
                            format="percent"
                            tooltip="Empirical anchor: many OECD net prices are far below US. Higher % = weaker savings."
                            onChange={v => updateConfig('internationalPricePctOfUS', v)}
                        />
                        <SliderControl
                            label="Domestic Value Ceiling (% of US)"
                            value={config.domesticValuePricePctOfUS ?? 0.65}
                            min={0.30} max={1.00} step={0.05}
                            format="percent"
                            tooltip="Proxy for a value-based ceiling. Higher % = weaker savings."
                            onChange={v => updateConfig('domesticValuePricePctOfUS', v)}
                        />
                        <hr className="divider" />
                        <h3><span className="control-group-icon">✅</span> Drug Ceiling Enforcement</h3>
                        <SliderControl
                            label="Enforce IRP"
                            value={config.enforceIRP ?? 1}
                            min={0} max={1} step={0.5}
                            format="percent"
                            tooltip="0% = IRP has no practical effect. 100% = IRP fully enforced."
                            onChange={v => updateConfig('enforceIRP', v)}
                        />
                        <SliderControl
                            label="Enforce Statutory Ceilings (DVB machinery)"
                            value={config.enforceDrugCeilings ?? 1}
                            min={0} max={1} step={0.5}
                            format="percent"
                            tooltip="0% = DVB/statutory ceiling machinery fails in practice. 100% = fully enforced."
                            onChange={v => updateConfig('enforceDrugCeilings', v)}
                        />
                    </div>
                )}

                {/* SHOCKS TAB */}
                {activeTab === 'shocks' && (
                    <div className="control-group">
                        <h3><span className="control-group-icon">⚡</span> External Shocks / Risks</h3>
                        <SliderControl
                            label="Legal Challenge Delay"
                            value={config.legalChallengeDelay}
                            min={0} max={5} step={1}
                            format="number"
                            suffix=" years"
                            tooltip="Implementation delay due to lawsuits"
                            onChange={v => updateConfig('legalChallengeDelay', v)}
                        />
                        <SliderControl
                            label="Rural Closures"
                            value={config.ruralHospitalClosureRate}
                            min={0} max={0.25} step={0.01}
                            format="percent"
                            tooltip="Hospital closure rate per 5 years"
                            onChange={v => updateConfig('ruralHospitalClosureRate', v)}
                        />
                        <SliderControl
                            label="Quality Decline"
                            value={config.providerQualityDecline}
                            min={0} max={0.20} step={0.01}
                            format="percent"
                            tooltip="Increase in complications"
                            onChange={v => updateConfig('providerQualityDecline', v)}
                        />
                    </div>
                )}

                {/* SCENARIOS TAB */}
                {activeTab === 'presets' && (
                    <div className="control-group">
                        <h3><span className="control-group-icon">📋</span> Scenario Library</h3>
                        <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1rem' }}>
                            Select a scenario to pre-configure policy settings and external events.
                        </p>

                        <div className="preset-buttons">
                            <button
                                className="preset-reset-btn"
                                onClick={onReset}
                                title="Reset all policy settings and events to statutory defaults"
                            >
                                <span>↺</span> Reset / Statutory Default
                            </button>

                            {/* Dynamically Render Groups */}
                            {[
                                { id: 'fiscal', label: 'Fiscal & Structural' },
                                { id: 'social', label: 'Social & Equity' },
                                { id: 'risk', label: 'Realism & Risks' },
                                { id: 'extreme', label: 'Extremes' }
                            ].map(group => {
                                const groupScenarios = SCENARIO_REGISTRY.filter(s => s.group === group.id);
                                if (groupScenarios.length === 0) return null;

                                return (
                                    <React.Fragment key={group.id}>
                                        <h4 className="preset-group-title">{group.label}</h4>
                                        {groupScenarios.map(scenario => (
                                            <button
                                                key={scenario.id}
                                                className={`preset-btn ${activeScenario === scenario.id ? 'active' : ''} ${scenario.id === 'full_obstruction' ? 'danger' : ''} ${scenario.id === 'utopia' ? 'success' : ''}`}
                                                onClick={() => onApplyScenario(scenario.id)}
                                                title={scenario.details}
                                            >
                                                <div className="preset-btn-header">
                                                    <strong>{scenario.icon} {scenario.label}</strong>
                                                </div>
                                                <span className="preset-desc">{scenario.description}</span>
                                            </button>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
